<!-- @format -->
<script lang="ts">
  import { getStoresContext } from "~/stores";
  import type { StoresKey } from "~/stores";
  import type { ManageAPI } from "~/manage-api/types";
  import { groupByInitial } from "~/util/store";
  import { getFragmentConfigOr } from "~/components/field-utils";
  import { CHILDREN_FOB_ID, getFobs } from "../../configuration";
  import { onMount } from "svelte";
  import ToggleButtonGroup from "./ToggleButtonGroup.svelte";
  import StoreCard from "./StoreCard.svelte";
  import StoreCategory from './StoreCategory.svelte';
  import StoreOpeningHours from './StoreOpeningHours.svelte';
  import type { ButtonItem, ButtonType } from "./types";
  
  export let request_store_key: StoresKey;
  export let fragment: Fragment;
  export let initialStores: ManageAPI.Store[] = [];
  export let initialDisplay: string = '';

  const stores_context = getStoresContext(request_store_key);
  const { stores, locale, T, scdata } = stores_context;
  const { getConfigWebContent } = stores_context.fieldUtils();
  const { childrenStores } = getConfigWebContent("childrenStores", fragment);
  const lineOfBusinessID: number = getFragmentConfigOr("lineOfBusinessID", fragment, 0);
  
  $: t = $T;
  $: fob_selected = lineOfBusinessID ? 
    [{ id: 'all_floor', type: 'floor' as ButtonType, text: t`all-floors` }] : 
    [{ id: 'toggle_all', type: 'fob' as ButtonType, text: t`fob-all` }, 
     { id: 'all_floor', type: 'floor' as ButtonType, text: t`all-floors` }];

  let display = '';
  let stores_filtered: ManageAPI.Store[] = [];
  let stores_by_initial: any;
  let search_term = '';
  let page = 1;
  let size = 45;
  let stores_shown: ManageAPI.Store[] = [];

  // Parse children stores configuration
  const childrenStoresIDs = typeof childrenStores === 'string' 
    ? childrenStores
        .replace(/\s+/g, "")
        .split(",")
        .map((i: string) => Number(i))
    : [];

  onMount(() => {
    // Initialize with data from server-side rendering if available
    display = initialDisplay || new URLSearchParams(location.search).get('display') || 'normal';
    
    // Use pre-filtered stores from server if available
    if (initialStores && initialStores.length > 0) {
      stores_filtered = [...initialStores];
      stores_shown = stores_filtered.slice(0, size);
      
      // Pre-compute alphabetical grouping if needed
      if (display === 'alphabetical-order' && $locale) {
        stores_by_initial = groupByInitial(stores_filtered, $locale);
      }
    }
  });

  // Event handlers
  function changeFilter(event: any) {
    search_term = '';
    fob_selected = event.detail;
  }
  
  function changeSearch(e: any) {
    search_term = e.detail;
  }
  
  function infiniteHandler() {
    page++;
    stores_shown = stores_filtered.slice(0, size * page);
  }

  // Filter stores by selected floor(s)
  function filterByFloor(fob_selected: any[]) {
    const floors = fob_selected
      .filter((i: any) => i.type === 'floor' && i.id !== 'all_floor')
      .map((i: any) => i.id);
    
    if (floors.length) {
      stores_filtered = stores_filtered.filter((store: ManageAPI.Store) => {
        if (!store.floor) return false;
        
        const storeFloorId = String(store.floor.id);
        return floors.some(floorId => String(floorId) === storeFloorId);
      });
    }
  }

  // Search filter function
  const includesTerm = (term: string) => ({ name, keywords }: ManageAPI.Store) => {
    return name.toLowerCase().includes(term.toLowerCase()) || 
           (keywords && keywords.toLowerCase().includes(term.toLowerCase()));
  };

  // Main reactive filtering logic
  $: {
    if ($locale && $stores.length) {
      const fobs = $scdata && getFobs($scdata);
      
      // Mark children stores with special FOB ID
      $stores.forEach((store: ManageAPI.Store) => {
        const matched = childrenStoresIDs.filter((id: number) => id === store.id).length;
        if (matched) {
          const { additionalFieldOfBusinessIds = [] } = store;
          additionalFieldOfBusinessIds.push(CHILDREN_FOB_ID);
          store.additionalFieldOfBusinessIds = additionalFieldOfBusinessIds;
        }
      });
      
      // --- CATEGORY PAGE FILTERING ---
      if (lineOfBusinessID && fobs && fobs.length > 0) {
        const matchingFob = fobs.find(fob => fob.id === lineOfBusinessID);
        
        if (matchingFob) {
          // 1. Filter by category (lineOfBusinessID)
          stores_filtered = [...$stores].filter(store => {
            const storeFobs = store.additionalFieldOfBusinessIds ? 
              [...store.additionalFieldOfBusinessIds, store.fieldOfBusinessId] : 
              [store.fieldOfBusinessId];
            
            return matchingFob.fob_ids.some(fobId => storeFobs.includes(fobId));
          });
          
          // 2. Then apply floor filters to the already category-filtered stores
          const hasFloorFilters = fob_selected.some(f => f.type === 'floor' && f.id !== 'all_floor');
          if (hasFloorFilters) {
            filterByFloor(fob_selected);
          }
        } else {
          console.error(`No FOB configuration found for lineOfBusinessID: ${lineOfBusinessID}`);
          stores_filtered = [];
        }
      } 
      // --- MAIN STORES PAGE FILTERING ---
      else {
        if (fob_selected.length) {    
          if (fob_selected[0].id === 'toggle_all') {
            // "All" FOB selected
            stores_filtered = [...$stores];
          } else {
            // Specific FOB categories selected
            const fobsFilter = fob_selected
              .filter((i: any) => i.type === 'fob' && i.id !== 'toggle_all')
              .map((i: any) => i.id);
              
            if (fobsFilter.length && fobs && fobs.length > 0) {
              const selectedFobs = fobs.filter(fob => fobsFilter.includes(fob.id));
              
              if (selectedFobs.length > 0) {
                const allInternalFobIds = selectedFobs.flatMap(fob => fob.fob_ids);
                
                stores_filtered = [...$stores].filter(store => {
                  const storeFobs = store.additionalFieldOfBusinessIds ? 
                    [...store.additionalFieldOfBusinessIds, store.fieldOfBusinessId] : 
                    [store.fieldOfBusinessId];
                  
                  return allInternalFobIds.some(fobId => storeFobs.includes(fobId));
                });
              } else {
                console.error(`No FOB configuration found for IDs: ${fobsFilter.join(', ')}`);
                stores_filtered = []; 
              }
            } else {
              stores_filtered = [...$stores];
            }
          }
          
          // Apply floor filters after FOB filtering
          filterByFloor(fob_selected);
        } else {
          stores_filtered = [...$stores];
        }
      }
      
      // Apply search term if needed
      if (search_term) {
        stores_filtered = $stores.filter(includesTerm(search_term));
      }

      // Update derived data
      stores_by_initial = groupByInitial(stores_filtered, $locale);
      page = 1;
      stores_shown = stores_filtered.slice(0, size);
    }
  }
</script>

{#if $stores && $locale}
  <div class="row">
    {#if display == 'normal'}
      <!-- Stores Grid View with Filters -->
      <div class="col-lg-4">
        <ToggleButtonGroup
          btn_selected={fob_selected}
          {request_store_key}
          {lineOfBusinessID}
          on:select={changeFilter}
          on:search={changeSearch}
        />
      </div>
      <div class="col-lg-8 mt-3 mt-lg-0 px-4 px-lg-2">
        <div class="row">
          {#if stores_shown.length}
            {#each stores_shown as store (store.id)}
              <StoreCard {request_store_key} {store} isSearchResult={false} />
            {/each}  
          {:else}
            <div class="col-12 text-center py-4">
              <p>{t`no-stores-found`}</p>
            </div>
          {/if}
        </div>
        {#if stores_shown.length < stores_filtered.length}
          <button
            on:click={infiniteHandler}
            type="button"
            class="show-more purple-outline-button mx-auto d-block fit-content"
          >
            {t`show-more`}
          </button>
        {/if}
      </div>
    {:else if display == 'alphabetical-order' && stores_by_initial.length}
      <!-- Alphabetical View -->
      {#each stores_by_initial as category}
        <StoreCategory {request_store_key} {category} />
      {/each}
    {:else if display == 'opening-hours' && stores_filtered.length}
      <!-- Opening Hours View -->
      {#each stores_filtered as store}
        <StoreOpeningHours {request_store_key} {store} />
      {/each}
    {/if}
  </div>  
{/if}

<style lang="scss">
  .store-category {
    padding: 0 30px;
    @media screen and (max-width: 768px) {
      text-align: center;
    }
    
    strong {
      display: block;
      margin: 15px 0;
      font-size: 30px;
      font-weight: 500;
      line-height: 22px;
    }
  }
</style>
